import { z } from "zod";
import {
  AllianceSchema,
  BaseSchema,
  BuildingSchema,
  CitySchema,
  PlayerSchema,
  PlayerWalletSchema,
  ServerSchema,
} from "./entities.js";

/** GET /api/health */
export const HealthResponseSchema = z.object({
  ok: z.literal(true),
  db: z.enum(["ok", "error"]),
  redis: z.enum(["ok", "error"]),
  version: z.string(),
  ts: z.number().int().nonnegative(),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/** GET /api/server-browser → list of servers + grouped cities (matches §3.5 reference UI). */
export const ServerBrowserCitySchema = CitySchema.pick({
  id: true,
  serverId: true,
  displayName: true,
  tier: true,
  role: true,
  faction: true,
  loadState: true,
});
export type ServerBrowserCity = z.infer<typeof ServerBrowserCitySchema>;

export const ServerBrowserEntrySchema = z.object({
  server: ServerSchema,
  cities: z.array(ServerBrowserCitySchema),
});
export type ServerBrowserEntry = z.infer<typeof ServerBrowserEntrySchema>;

export const ServerBrowserResponseSchema = z.object({
  servers: z.array(ServerBrowserEntrySchema),
});
export type ServerBrowserResponse = z.infer<typeof ServerBrowserResponseSchema>;

/** POST /api/auth/register */
export const RegisterRequestSchema = z.object({
  handle: z.string().min(2).max(32),
  password: z.string().min(8).max(128),
  faction: PlayerSchema.shape.faction,
  serverId: z.number().int().positive(),
  cityId: z.string().min(1),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

/** POST /api/auth/login */
export const LoginRequestSchema = z.object({
  handle: z.string().min(2).max(32),
  password: z.string().min(8).max(128),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/** GET /api/auth/me */
export const MeResponseSchema = z.object({
  player: PlayerSchema,
  base: BaseSchema,
  wallet: PlayerWalletSchema,
  alliance: AllianceSchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** GET /api/world/viewport — Phase 0 stub returns empty payload. */
export const ViewportResponseSchema = z.object({
  serverId: z.number().int().positive(),
  centerLat: z.number(),
  centerLon: z.number(),
  cities: z.array(CitySchema),
  bases: z.array(BaseSchema),
});
export type ViewportResponse = z.infer<typeof ViewportResponseSchema>;

/** GET /api/base/:baseId/buildings — §5 building roster for the §6.1 info popup. */
export const BaseBuildingsResponseSchema = z.object({
  baseId: z.string().uuid(),
  buildings: z.array(BuildingSchema),
});
export type BaseBuildingsResponse = z.infer<typeof BaseBuildingsResponseSchema>;
